import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Brain,
  Blocks,
  Building2,
  CalendarRange,
  ChevronDown,
  ClipboardList,
  Compass,
  Cpu,
  FileText,
  Globe,
  GitBranch,
  HelpCircle,
  Lightbulb,
  Mail,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  TextCursorInput,
  Users2,
  type LucideIcon,
} from 'lucide-react';
import {
  BLUEPRINT_SLUG_LABELS,
  BLUEPRINT_SLUG_ORDER,
  KNOWLEDGE_APPLIES_LABELS,
  KNOWLEDGE_APPLIES_ORDER,
  KNOWLEDGE_APPLIES_SLUGS,
  type BrainBlueprintSlug,
  type KnowledgeAppliesTo,
} from '@/modules/brain/services/brain.api';

const BLUEPRINT_ICON: Record<BrainBlueprintSlug, LucideIcon> = {
  email: Mail,
  job: ClipboardList,
  icp: Star,
  pipeline: GitBranch,
  evaluation: ShieldCheck,
  'question-set': HelpCircle,
  'custom-field': TextCursorInput,
};

const KNOWLEDGE_ICON: Record<KnowledgeAppliesTo, LucideIcon> = {
  global: Globe,
  icp_agent: Star,
  cv_screener: Search,
  pipeline_agent: Cpu,
};

const STORAGE_KEY = 'brain-rail-open-groups';
type GroupId = 'company' | 'memory' | 'skills' | 'blueprints' | 'knowledge';

function readOpen(): Record<GroupId, boolean> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { company: true, memory: true, skills: true, blueprints: true, knowledge: true };
    const parsed = JSON.parse(raw);
    return {
      company: parsed.company !== false,
      memory: parsed.memory !== false,
      skills: parsed.skills !== false,
      blueprints: parsed.blueprints !== false,
      knowledge: parsed.knowledge !== false,
    };
  } catch {
    return { company: true, memory: true, skills: true, blueprints: true, knowledge: true };
  }
}

function writeOpen(state: Record<GroupId, boolean>) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures.
  }
}

export function BrainRail() {
  const location = useLocation();
  const [open, setOpen] = useState<Record<GroupId, boolean>>(() => readOpen());

  // Auto-expand the group whose route is currently active.
  useEffect(() => {
    if (location.pathname.startsWith('/brain/company') && !open.company) {
      setOpen((prev) => ({ ...prev, company: true }));
    }
    if (location.pathname.startsWith('/brain/memory') && !open.memory) {
      setOpen((prev) => ({ ...prev, memory: true }));
    }
    if (location.pathname.startsWith('/brain/skills') && !open.skills) {
      setOpen((prev) => ({ ...prev, skills: true }));
    }
    if (location.pathname.startsWith('/brain/blueprints') && !open.blueprints) {
      setOpen((prev) => ({ ...prev, blueprints: true }));
    }
    if (location.pathname.startsWith('/brain/knowledge') && !open.knowledge) {
      setOpen((prev) => ({ ...prev, knowledge: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function toggle(group: GroupId) {
    setOpen((prev) => {
      const next = { ...prev, [group]: !prev[group] };
      writeOpen(next);
      return next;
    });
  }

  return (
    <nav className="brain-rail" aria-label="Brain navigation">
      <header className="brain-rail-header">
        <Brain size={14} strokeWidth={1.7} />
        <span>Brain</span>
      </header>

      {/* ── YC demo: Company group (org context Jacinta references) ──── */}
      <Group
        id="company"
        label="Company"
        icon={Building2}
        open={open.company}
        onToggle={() => toggle('company')}
      >
        <NavLink
          to="/brain/company/profile"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <Building2 size={14} strokeWidth={1.6} />
          </span>
          <span>Organization</span>
        </NavLink>
        <NavLink
          to="/brain/company/philosophy"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <Compass size={14} strokeWidth={1.6} />
          </span>
          <span>Hiring philosophy</span>
        </NavLink>
        <NavLink
          to="/brain/company/headcount"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <CalendarRange size={14} strokeWidth={1.6} />
          </span>
          <span>Headcount plan</span>
        </NavLink>
        <NavLink
          to="/brain/company/employment-history"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <Users2 size={14} strokeWidth={1.6} />
          </span>
          <span>Employment history</span>
        </NavLink>
      </Group>
      {/* ── /YC demo: Company group ──────────────────────────────────── */}

      {/* ── YC demo: Plan 05 (Memory) ────────────────────────────────── */}
      <Group
        id="memory"
        label="Memory"
        icon={Sparkles}
        open={open.memory}
        onToggle={() => toggle('memory')}
      >
        <NavLink
          to="/brain/memory"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <Sparkles size={14} strokeWidth={1.6} />
          </span>
          <span>Things Lantern has learned</span>
        </NavLink>
      </Group>
      {/* ── /YC demo: Plan 05 ────────────────────────────────────────── */}

      <Group
        id="skills"
        label="Skills"
        icon={Blocks}
        open={open.skills}
        onToggle={() => toggle('skills')}
      >
        <NavLink
          to="/brain/skills"
          className={({ isActive }) =>
            isActive ? 'brain-rail-item active' : 'brain-rail-item'
          }
        >
          <span className="brain-rail-item-icon">
            <Blocks size={14} strokeWidth={1.6} />
          </span>
          <span>Skills</span>
        </NavLink>
      </Group>

      <Group
        id="blueprints"
        label="Blueprints"
        icon={Cpu /* not actually rendered; only the chevron + label */}
        open={open.blueprints}
        onToggle={() => toggle('blueprints')}
      >
        {BLUEPRINT_SLUG_ORDER.map((slug) => {
          const Icon = BLUEPRINT_ICON[slug];
          return (
            <NavLink
              key={slug}
              to={`/brain/blueprints/${slug}`}
              className={({ isActive }) =>
                isActive ? 'brain-rail-item active' : 'brain-rail-item'
              }
            >
              <span className="brain-rail-item-icon">
                <Icon size={14} strokeWidth={1.6} />
              </span>
              <span>{BLUEPRINT_SLUG_LABELS[slug]}</span>
            </NavLink>
          );
        })}
      </Group>

      <Group
        id="knowledge"
        label="Knowledge"
        icon={Lightbulb}
        open={open.knowledge}
        onToggle={() => toggle('knowledge')}
      >
        {KNOWLEDGE_APPLIES_ORDER.map((applies) => {
          const Icon = KNOWLEDGE_ICON[applies];
          const slug = KNOWLEDGE_APPLIES_SLUGS[applies];
          return (
            <NavLink
              key={applies}
              to={`/brain/knowledge/${slug}`}
              className={({ isActive }) =>
                isActive ? 'brain-rail-item active' : 'brain-rail-item'
              }
            >
              <span className="brain-rail-item-icon">
                <Icon size={14} strokeWidth={1.6} />
              </span>
              <span>{KNOWLEDGE_APPLIES_LABELS[applies]}</span>
            </NavLink>
          );
        })}
      </Group>
    </nav>
  );
}

function Group({
  id,
  label,
  open,
  onToggle,
  children,
}: {
  id: GroupId;
  label: string;
  icon: LucideIcon;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={open ? 'brain-rail-group open' : 'brain-rail-group'}>
      <button
        type="button"
        className="brain-rail-group-header"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`brain-rail-${id}`}
      >
        <ChevronDown size={12} strokeWidth={2} className="brain-rail-group-chevron" />
        <span>{label}</span>
      </button>
      {open && (
        <div id={`brain-rail-${id}`} className="brain-rail-group-items">
          {children}
        </div>
      )}
    </section>
  );
}
